import styled, { useTheme } from 'styled-components'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'

import remarkGfm from 'remark-gfm'

import { memo } from 'react'
import ExpandableList from '../ChatComponents/ListComponents/ExpandableList'
import CardListItem from '../ChatComponents/ListComponents/CardListItem'

const ChatMarkdown = ({ children }: { children: string }) => {
    const theme = useTheme()

    const transformMarkdownContent = (content: string): string => {
        // Remove line breaks within square brackets
        const cleanedContent = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, p1, p2) => {
            const cleanedText = p1.replace(/\n/g, ' ')
            return `[${cleanedText}](${p2})`
        })

        // Split the content by newlines to process each line separately
        const lines = cleanedContent.split('\n')

        // Transform lines that match the pattern [text](url) into list items
        const transformedLines = lines.map(line => {
            const linkPattern = /^\[.+\]\(.+\)$/
            const headingPattern =
                /^\*\*(Twitter Summary:|Key Tweets:|Other Headings:|Search Summary:|Discord Messages:|Reddit Summary:|Hacker News Summary:|Explanation:|Summary:)\*\*\s*$/ // Updated to match bold headings

            // Add a newline before recognized headings
            if (headingPattern.test(line.trim())) {
                return `\n${line.trim()}` // Add a newline before the heading
            }

            return linkPattern.test(line.trim()) ? `- ${line.trim()}` : line.trim()
        })

        // Join the lines back into a single string
        return transformedLines.join('\n')
    }

    const processedContent = transformMarkdownContent(children)

    return (
        <StyledReactMarkdown
            remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
            components={{
                img: props => <StyledImg {...props} />,
                table: props => <StyledTable {...props} />,
                a: ({ href, children }) => {
                    return <a href={href as string}>{children}</a>
                },
                code({ inline, className, children, style, ...props }) {
                    const match = /language-(\w+)/.exec(className || 'language-js')
                    return !inline && match ? (
                        <SyntaxHighlighter
                            style={theme?.body.markdownColor || style}
                            language={match[1]}
                            PreTag='div'
                            {...props}
                        >
                            {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                    ) : (
                        <code className={className} {...props}>
                            {children}
                        </code>
                    )
                },

                li: ({ children, ...props }) => {
                    return <CardListItem props={props}>{children}</CardListItem>
                },
                ul: ({ children, ...props }) => {
                    return <ExpandableList props={props}>{children}</ExpandableList>
                },
                ol: ({ children, ...props }) => {
                    return <ExpandableList props={props}>{children}</ExpandableList>
                },

                strong: ({ children }) => {
                    if (children[0] === 'Key Sources:') {
                        return (
                            <StyledTitle>
                                {/* <WebSearchLoader loading={typing} /> */}
                                <strong>Web Search</strong>
                            </StyledTitle>
                        )
                    }
                    if (children[0] === 'Key Tweets:') {
                        return (
                            <StyledTitle>
                                {/* <TwitterLoader loading={typing} /> */}
                                <strong>Twitter</strong>
                            </StyledTitle>
                        )
                    }
                    if (children[0] === 'Key Posts:') {
                        return (
                            <StyledTitle>
                                {/* <RedditLoader loading={typing} /> */}
                                <strong>Reddit</strong>
                            </StyledTitle>
                        )
                    }
                    if (children[0] === 'Discord Search Summary:') {
                        return (
                            <>
                                <StyledTitle>
                                    {/* <DiscordLoader loading={typing} /> */}
                                    <strong>Bittensor Discord</strong>
                                </StyledTitle>
                            </>
                        )
                    }
                    if (children[0] === 'Bittensor Documentation Summary:') {
                        return (
                            <>
                                <StyledTitle>
                                    {/* <BittensorLoader loading={typing} /> */}
                                    <strong>Bittensor Documentation</strong>
                                </StyledTitle>
                            </>
                        )
                    }
                    if (children[0] === 'Key News:') {
                        return (
                            <StyledTitle>
                                {/* <HackerNewsLoader loading={typing} /> */}
                                <strong>Hacker News</strong>
                            </StyledTitle>
                        )
                    }
                    if (children[0] === 'Search Summary:') {
                        return <div />
                    }
                    if (children[0] === 'Twitter Summary:') {
                        return <div />
                    }
                    if (children[0] === 'Discord Messages:') {
                        return <div />
                    }
                    if (children[0] === 'Reddit Summary:') {
                        return <div />
                    }
                    if (children[0] === 'Hacker News Summary:') {
                        return <div />
                    }
                    if (children[0] === 'Explanation:') {
                        return <div />
                    }
                    if (children[0] === 'Summary:') {
                        return (
                            <StyledTitle>
                                {/* <FinalAnswerLoader loading={typing} /> */}
                                <strong>Final Answer</strong>
                            </StyledTitle>
                        )
                    }

                    return <StyledTitle>{children}</StyledTitle>
                },
            }}
        >
            {processedContent}
        </StyledReactMarkdown>
    )
}

export default memo(ChatMarkdown)

const StyledReactMarkdown = styled(ReactMarkdown)`
    color: ${({ theme }) => theme.body.textColorPrimary};
    display: flex;
    flex-direction: column;
    gap: 10px;

    font-weight: 400;
    font-style: normal;
    font-size: 14px;

    white-space: pre-line;

    word-wrap: break-word; // Break long words to fit within the container
    word-break: break-all; // Break long words at any character to fit within the container
    overflow-wrap: break-word;
`

const StyledTable = styled.table`
    border-collapse: collapse;

    th,
    td {
        border: 1px solid #fff;
        padding: 5px 30px;
        text-align: center;
    }
`

const StyledImg = styled.img`
    margin-top: 5px;

    width: 300px;
    height: 300px;

    border-radius: 8px;
`
const StyledTitle = styled.div`
    color: ${({ theme }) => theme.body.textColorPrimary};
    font-weight: 500;

    font-size: 1rem; /* text-base */
    @media (min-width: 768px) {
        font-size: 1rem; /* md:text-lg */
    }
    display: flex; /* flex */
    gap: 0.25rem; /* gap-1 */
    align-items: center; /* items-center */

    /* margin-top: 16px; */
    /* margin-bottom: -10px; */
`
